import { ViewportPortal, useReactFlow, Position, type HandleType, type InternalNode } from '@xyflow/react';
import { useLayoutEffect, useState } from 'react';
import * as Y from 'yjs';
import type { CanvasAwarenessState, SharedInFlightEdge } from './awareness-types';
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from "@/features/canvas/engine/wiring/canvas-roots";
import { useSmoothing } from './use-smoothing';
import {
  DEFAULT_EDGE_DATA,
  dashArrayFor,
  pathFor,
  type EdgeVisualData,
} from '../edges/edge-visuals';
import overlayStyles from './awareness-overlay.module.css';

export interface RemoteInFlightEdgesProps {
  remoteStates: Map<number, CanvasAwarenessState>;
}

/**
 * Renders ghost edges for in-flight drags happening on remote peers. Mirrors
 * NulaConnectionLine's path/stroke logic so a peer's ghost looks identical to
 * the rubber-band the local user sees during their own drag.
 *
 * Source of truth for the path:
 *   - Fixed end: getInternalNode(source).handleBounds — same lookup the
 *     pro example's ConnectionEdge does.
 *   - Moving end: snapped target handle if peer's `target/targetHandle` is set,
 *     otherwise raw cursor `position` (flow-space).
 *
 * Mounts inside ViewportPortal so coordinates stay in flow-space and React
 * Flow's pan/zoom transform applies automatically.
 */
export function RemoteInFlightEdges({ remoteStates }: RemoteInFlightEdgesProps) {
  const visible: Array<{ id: number; state: CanvasAwarenessState }> = [];
  remoteStates.forEach((state, id) => {
    if (!state.canvasInFlightEdge) return;
    visible.push({ id, state });
  });

  if (visible.length === 0) return null;

  return (
    <ViewportPortal>
      {visible.map(({ id, state }) => (
        <RemoteInFlightEdge
          key={id}
          edge={state.canvasInFlightEdge!}
          color={state.user.color}
        />
      ))}
    </ViewportPortal>
  );
}

function RemoteInFlightEdge({ edge, color }: { edge: SharedInFlightEdge; color: string }) {
  const { getInternalNode } = useReactFlow();
  const doc = useCanvasDoc();
  const binding = useCanvasBinding();

  const sourceNode = getInternalNode(edge.source);

  const sourceHandle = sourceNode
    ? findHandle(sourceNode, edge.sourceType, edge.sourceHandle)
    : null;

  // Resolve the moving end. If a target handle is snapped, use its bounds
  // (so the ghost snaps cleanly when the peer is over a valid handle);
  // otherwise track the raw cursor position.
  const target = sourceHandle
    ? resolveTargetEnd(getInternalNode, sourceHandle.position, edge)
    : null;

  // Smooth the moving end through perfect-cursors. Awareness fires at our
  // 30Hz throttle; the spline interpolates to ~60Hz so the ghost edge
  // doesn't look stuttery.
  const [smoothedTarget, setSmoothedTarget] = useState<{ x: number; y: number }>(
    () => (target ? { x: target.x, y: target.y } : { x: edge.position.x, y: edge.position.y }),
  );
  const onMove = useSmoothing((p) => setSmoothedTarget({ x: p[0], y: p[1] }));
  useLayoutEffect(() => {
    if (target) onMove([target.x, target.y]);
  }, [onMove, target?.x, target?.y]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sourceNode || !sourceHandle || !target) return null;

  const sourceX =
    sourceNode.internals.positionAbsolute.x + sourceHandle.x + sourceHandle.width * 0.5;
  const sourceY =
    sourceNode.internals.positionAbsolute.y + sourceHandle.y + sourceHandle.height * 0.5;
  const sourcePosition = sourceHandle.position;

  const visual = readSourceEdgeVisuals(doc, edge, binding);

  const [path] = pathFor(visual.curvature, {
    sourceX,
    sourceY,
    sourcePosition,
    targetX: smoothedTarget.x,
    targetY: smoothedTarget.y,
    targetPosition: target.position,
  });

  const dashArray = dashArrayFor(visual.strokeStyle, visual.strokeWidth);
  const stroke = color;

  return (
    <svg className={overlayStyles.overlay} width={0} height={0}>
      <g opacity={0.7}>
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={visual.strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          {...(dashArray ? { strokeDasharray: dashArray } : {})}
        />
        <circle cx={sourceX} cy={sourceY} r={5} fill={stroke} />
        <circle cx={smoothedTarget.x} cy={smoothedTarget.y} r={5} fill={stroke} />
      </g>
    </svg>
  );
}

function findHandle(
  node: InternalNode,
  type: HandleType,
  handleId: string | null | undefined,
) {
  const handles = node.internals.handleBounds?.[type];
  if (!handles || handles.length === 0) return null;
  const matched = handles.find((h) => h.id === (handleId ?? null));
  return matched ?? handles[0];
}

function getOppositePosition(p: Position): Position {
  switch (p) {
    case Position.Top: return Position.Bottom;
    case Position.Bottom: return Position.Top;
    case Position.Left: return Position.Right;
    case Position.Right: return Position.Left;
    default: return p;
  }
}

function resolveTargetEnd(
  getInternalNode: (id: string) => InternalNode | undefined,
  sourcePosition: Position,
  edge: SharedInFlightEdge,
): { x: number; y: number; position: Position } {
  const fallbackPosition = getOppositePosition(sourcePosition);
  const fallback: { x: number; y: number; position: Position } = {
    x: edge.position.x,
    y: edge.position.y,
    position: fallbackPosition,
  };

  if (!edge.target || !edge.targetType) return fallback;
  const targetNode = getInternalNode(edge.target);
  if (!targetNode) return fallback;
  const handle = findHandle(targetNode, edge.targetType, edge.targetHandle);
  if (!handle) return fallback;
  return {
    x: targetNode.internals.positionAbsolute.x + handle.x + handle.width * 0.5,
    y: targetNode.internals.positionAbsolute.y + handle.y + handle.height * 0.5,
    position: handle.position,
  };
}

/**
 * For reconnect drags (peer is re-anchoring an existing edge), look up that
 * edge's persisted visual data so the ghost reflects its real stroke / dash /
 * curvature. We can't tell from SharedInFlightEdge alone whether this is a
 * fresh-create or reconnect — but we can probe the edges Y.Map: if there's
 * an edge whose source/sourceHandle match the fixed end, use its visuals.
 *
 * Falls back to DEFAULT_EDGE_DATA when no match is found (fresh-create drag).
 */
function readSourceEdgeVisuals(
  doc: Y.Doc | null,
  inFlight: SharedInFlightEdge,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): EdgeVisualData {
  if (!doc) return DEFAULT_EDGE_DATA;
  const edges = canvasRoots(doc, binding).edges;
  let match: Y.Map<unknown> | null = null;
  edges.forEach((e) => {
    if (match) return;
    const matchSrcEnd =
      inFlight.sourceType === 'source' &&
      e.get('source') === inFlight.source &&
      (e.get('sourceHandle') ?? null) === inFlight.sourceHandle;
    const matchTgtEnd =
      inFlight.sourceType === 'target' &&
      e.get('target') === inFlight.source &&
      (e.get('targetHandle') ?? null) === inFlight.sourceHandle;
    if (matchSrcEnd || matchTgtEnd) match = e;
  });
  if (!match) return DEFAULT_EDGE_DATA;
  const data = (match as Y.Map<unknown>).get('data');
  if (!(data instanceof Y.Map)) return DEFAULT_EDGE_DATA;
  const merged: Record<string, unknown> = { ...DEFAULT_EDGE_DATA };
  data.forEach((v, k) => { merged[k] = v; });
  if (merged.stroke === 'var(--text-1)') merged.stroke = 'var(--on-surface)';
  return merged as EdgeVisualData;
}
